import type { StateVersionSummary } from '@/lib/apiTypes'
import { formatDateTime, formatDuration } from '@/lib/format'
import { ScanStatusBadge } from '@/shared/components/ScanStatusBadge'
import { PushedByCell } from '@/features/state/components/PushedByCell'
import { IconButton } from '@/shared/components/IconButton'
import { RestoreIcon } from '@/shared/components/Icons'

/**
 * The metadata for the SELECTED serial, carrying the columns the rail is too narrow to show.
 * Nothing from the old version table is lost here - it moves from a row to a header, which is also
 * what makes "which serial am I looking at" answerable without scrolling back to the list.
 */
export function SerialPaneHeader({ v, isLatest, onPromote }: {
  v: StateVersionSummary
  isLatest: boolean
  onPromote: (() => void) | null
}) {
  return (
    <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--head)', fontWeight: 700, fontSize: '0.95rem' }}>Serial {v.serial}</span>
        {isLatest && <span className="badge badge-accent" style={{ fontSize: '0.55rem' }}>current</span>}
        {v.promoted_from_serial != null && (
          <span className="badge badge-neutral" style={{ fontSize: '0.55rem' }} title="Restored from an earlier version">
            restored from {v.promoted_from_serial}
          </span>
        )}
        <ScanStatusBadge status={v.scan_status} critical={v.critical_count} high={v.high_count} />
        {(v.policy_total_count ?? 0) > 0 && (
          (v.policy_failing_count ?? 0) > 0
            ? <span className="badge badge-red">{v.policy_failing_count} failing</span>
            : <span className="badge badge-green">✓ compliant</span>
        )}
        <span style={{ flex: 1 }} />
        {/* Promoting the latest version is a no-op, so the action only exists on older serials. */}
        {onPromote && !isLatest && (
          <IconButton label="Promote" tip="Promote this version to latest (restore)" onClick={onPromote}>
            <RestoreIcon />
          </IconButton>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--text2)' }}>
        <span style={{ fontFamily: 'var(--mono)' }}>
          {v.resource_count} resources
          {(v.real_resource_count ?? null) !== null && (v.real_resource_count ?? 0) + (v.internal_resource_count ?? 0) > 0 && (
            <span className="dim"> ({v.real_resource_count} real · {v.internal_resource_count} internal)</span>
          )}
        </span>
        <span style={{ fontFamily: 'var(--mono)' }}>{formatDuration(v.push_duration_ms)}</span>
        <span>{formatDateTime(v.pushed_at)}</span>
        <PushedByCell
          actorType={v.actor_type}
          actorName={v.actor_name}
          vcsProvider={v.vcs_provider}
          vcsRepository={v.vcs_repository}
          vcsRef={v.vcs_ref}
          vcsRefType={v.vcs_ref_type}
          vcsSha={v.vcs_sha}
          vcsCommitUrl={v.vcs_commit_url}
        />
      </div>
    </div>
  )
}
